
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Spin, Upload, Progress } from "antd";
import * as recruitmentApi from "../../../../core/data/redux/services/recruitmentApi";
import { notify } from "../../../../services/toast.config";

const { Dragger } = Upload;

const CandidateUploadPortal: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();

  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);

  useEffect(() => {
    if (candidateId) loadRequests(Number(candidateId));
  }, [candidateId]);

  const loadRequests = async (id: number) => {
    setLoading(true);
    try {
      const data = await recruitmentApi.getCandidatePendingRequests(id);
      setRequests(data || []);
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to load document requests");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File, documentType: string) => {
    setUploading(documentType);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", documentType);
    fd.append("candidate_id", candidateId || "");

    try {
      await recruitmentApi.candidateUploadDocument(fd);
      notify.success(`${documentType} uploaded successfully`);
      if (candidateId) loadRequests(Number(candidateId));
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "#f8f9fa" }}>
        <Spin size="large" />
      </div>
    );
  }

  const approvedCount = requests.filter(req => req.status === "Approved").length;
  const progressPercent = requests.length > 0 ? Math.round((approvedCount / requests.length) * 100) : 100;

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100vh" }}>
      
      <div
        className="text-white py-5 position-relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)" }}
      >
        <div className="position-absolute" style={{ top: -100, right: -50, width: 300, height: 300, background: "rgba(255,255,255,0.03)", borderRadius: "50%" }}></div>
        <div className="container position-relative z-index-1">
          <div className="row align-items-center">
            <div className="col-md-7 mb-4 mb-md-0">
              <span className="badge bg-white text-dark rounded-pill px-3 py-2 mb-3 shadow-sm fw-semibold">
                <i className="ti ti-shield-lock me-2 text-success" /> Secure Portal
              </span>
              <h1 className="fw-bolder display-5 mb-2 text-white shadow-sm">Document Request Center</h1>
              <p className="fs-5 text-white-50 mb-0" style={{ maxWidth: 500 }}>
                Please upload the required documents to keep your application moving forward.
              </p>
            </div>
            <div className="col-md-5">
              <div className="card border-0 bg-white bg-opacity-10 shadow-lg text-white p-4 rounded-4" style={{ backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold">Total Progress</span>
                  <span className="fs-5 fw-bold">{progressPercent}%</span>
                </div>
                <Progress percent={progressPercent} strokeColor={{ '0%': '#10b981', '100%': '#34d399' }} showInfo={false} trailColor="rgba(255,255,255,0.2)" />
                <div className="mt-3 text-white-50 fs-13">
                  <i className="ti ti-check me-1" /> {approvedCount} of {requests.length} documents approved
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-5" style={{ marginTop: "-30px" }}>
        {requests.length > 0 ? (
          <div className="row g-4">
            {requests.map((req: any) => (
              <div key={req.document_type} className="col-lg-6">
                <div
                  className="card border-0 h-100 rounded-4 overflow-hidden"
                  style={{
                    boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "none"}
                >
                  <div className="card-body p-4 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light">
                      <div className="d-flex align-items-center">
                        <div className={`p-3 rounded-circle me-3 flex-shrink-0 bg-opacity-10 ${req.status === "Approved" ? "bg-success text-success"
                          : req.status === "Rejected" ? "bg-danger text-danger"
                            : "bg-primary text-primary"
                          }`} style={{ width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className={`ti fs-4 ${req.status === "Approved" ? "ti-circle-check"
                            : req.status === "Uploaded" ? "ti-cloud-up"
                              : "ti-file-description"
                            }`} />
                        </div>
                        <div>
                          <h5 className="fw-bold mb-1 text-dark">{req.document_type}</h5>
                          <span className={`badge rounded-pill fw-medium ${req.status === "Approved" ? "bg-success bg-opacity-10 text-success"
                            : req.status === "Rejected" ? "bg-danger bg-opacity-10 text-danger"
                              : req.status === "Uploaded" ? "bg-info bg-opacity-10 text-info"
                                : "bg-warning bg-opacity-10 text-warning"
                            }`}>
                            <i className="ti ti-point-filled me-1" /> {req.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex-grow-1">
                      {req.rejection_reason && (
                        <div className="alert alert-danger fs-14 py-3 mb-4 rounded-3 d-flex border-0 bg-danger bg-opacity-10">
                          <i className="ti ti-alert-triangle fs-4 me-2 text-danger" />
                          <div>
                            <span className="fw-bold d-block text-danger mb-1">Upload Rejected</span>
                            <span className="text-danger opacity-75">{req.rejection_reason}</span>
                          </div>
                        </div>
                      )}

                      {req.status !== "Approved" && (
                        <Dragger
                          name="file"
                          multiple={false}
                          showUploadList={false}
                          customRequest={({ file }: any) => handleUpload(file, req.document_type)}
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="w-100"
                          style={{ background: "#f8fafc", borderColor: "#cbd5e1", padding: "20px" }}
                        >
                          {uploading === req.document_type ? (
                            <div className="py-4 text-center">
                              <Spin size="large" className="mb-3 d-block" />
                              <p className="text-primary fw-medium m-0">Securely uploading...</p>
                            </div>
                          ) : (
                            <div className="py-3 text-center">
                              <i className="ti ti-cloud-upload text-primary mb-3 d-block" style={{ fontSize: "40px" }} />
                              <h6 className="fw-bold text-dark mb-2">Drag & Drop to Upload</h6>
                              <p className="text-muted fs-13 m-0">or click to browse PDF, JPG, PNG (max 10MB)</p>
                            </div>
                          )}
                        </Dragger>
                      )}

                      {req.status === "Approved" && (
                        <div className="text-center py-5 bg-success bg-opacity-10 rounded-3 h-100 d-flex flex-column align-items-center justify-content-center">
                          <div className="bg-success text-white p-3 rounded-circle mb-3 shadow-sm d-inline-flex" style={{ width: 60, height: 60, alignItems: "center", justifyContent: "center" }}>
                            <i className="ti ti-check fs-2" />
                          </div>
                          <h5 className="text-success fw-bold mb-2">Document Verified</h5>
                          <p className="text-success text-opacity-75 fs-14 mb-0 px-4">This document has been successfully reviewed and approved by our team.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="ti ti-checkbox fs-1 text-success d-block mb-3" />
            <h5>No pending document requests</h5>
            <p className="text-muted">All requested documents have been submitted.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateUploadPortal;
